import { useEffect, useState } from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Pencil } from 'lucide-react'
import type {
  PlanningConstraintKey, ProjectConstraintRuleDto, ProjectDetailAlertDto, ConstraintWorkItemKind,
} from '../../../../lib/api/v2/planning'
import * as planningApi from '../../../../lib/api/v2/planning'
import { cn } from '../../../../lib/utils'

/**
 * « Délais à respecter » block (SOLY-PLAN-001 MS-26, design Round 4 §panneau détail).
 * Rendered inside the Métré / Pose « voir le détail » panel, between the ready-check and the
 * actions. Collapsed by default with a sober summary (amber « N à risque » / grey « tous respectés »).
 * Each rule shows a complete human sentence + a discreet text status (Respecté / À risque), an inline
 * per-line editor (new value + required reason), a « Pas un souci ici » dismissal, and — for an
 * overridden/dismissed rule — a « revenir au défaut » reset. Values come from MS-25's constraints
 * endpoints; the at-risk status is derived from the rule's mapped alert code being present in the
 * cockpit detail alerts (the BE does not return a remaining-days countdown).
 */

type Section = 'metre' | 'pose' | 'commandes'

const SECTION_KIND: Record<Section, ConstraintWorkItemKind> = {
  metre: 'TechnicalSurvey',
  pose: 'Installation',
  commandes: 'Installation',
}

/** The order keys live in the COMMANDES section, not Pose (both are Installation-kind BE-side). */
const ORDER_KEYS: ReadonlySet<PlanningConstraintKey> = new Set([
  'OrderSendLeadDays', 'SupplierConfirmDelayDays', 'DeliveryBeforePoseLeadDays', 'OrderTriageDelayDays',
])

function belongsToSection(key: PlanningConstraintKey, kind: ConstraintWorkItemKind, section: Section): boolean {
  if (kind !== SECTION_KIND[section]) return false
  if (section === 'commandes') return ORDER_KEYS.has(key)
  if (section === 'pose') return !ORDER_KEYS.has(key)
  return true
}

/** Human FR sentence per rule. `{days}` is interpolated with the effective value. */
const RULE_SENTENCE: Record<PlanningConstraintKey, (days: number) => string> = {
  SurveyLeadMaxDaysBeforePose: (d) => `Le métré doit être fait au plus tard ${d} jours ouvrables avant la pose.`,
  SurveyLeadMaxDaysEarlyBeforePose: (d) => `Le métré ne doit pas être fait plus de ${d} jours ouvrables avant la pose.`,
  SurveyorAssignLeadDays: (d) => `Le métreur doit être choisi au moins ${d} jours ouvrables avant le métré.`,
  SurveyClientConfirmLeadDays: (d) => `La date doit être validée avec le client au moins ${d} jours ouvrables avant le métré.`,
  InstallerAssignLeadDays: (d) => `Les placeurs doivent être choisis au moins ${d} jours ouvrables avant la pose.`,
  PoseClientConfirmLeadDays: (d) => `La date doit être validée avec le client au moins ${d} jours ouvrables avant la pose.`,
  // SOLY-PO-001 MS-05 — commandes
  OrderSendLeadDays: (d) => `Pour être livré avant la pose, envoyez les commandes au moins ${d} jours ouvrables à l'avance.`,
  SupplierConfirmDelayDays: (d) => `La confirmation fournisseur est attendue sous ${d} jours ouvrables après l'envoi.`,
  DeliveryBeforePoseLeadDays: (d) => `La livraison doit arriver au moins ${d} jours ouvrables avant la pose.`,
  OrderTriageDelayDays: (d) => `Un article à commander doit être traité sous ${d} jours ouvrables.`,
}

/**
 * Alert code that flags a rule « à risque » — the rule's OWN alert, per the _constraints.md
 * codes↔keys contract (mirrors the BE ConstraintDefinitions registry; every key has one).
 * Pre-session bug fixed here: SurveyLeadMaxDaysBeforePose was mapped to #1 (an existence rule that
 * never evaluated the gap) and the « early » line was hard-coded « Respecté » — the cockpit lied.
 */
const RULE_ALERT_CODE: Record<PlanningConstraintKey, number> = {
  SurveyLeadMaxDaysBeforePose: 15,
  SurveyLeadMaxDaysEarlyBeforePose: 16,
  SurveyorAssignLeadDays: 2,
  SurveyClientConfirmLeadDays: 3,
  InstallerAssignLeadDays: 10,
  PoseClientConfirmLeadDays: 11,
  // SOLY-PO-001 MS-05 — commandes (codes redefined/new in the C catalogue)
  OrderSendLeadDays: 6,
  SupplierConfirmDelayDays: 7,
  DeliveryBeforePoseLeadDays: 12,
  OrderTriageDelayDays: 19,
}

/** A rule is at risk when its own alert code is present among the project's alerts. */
function isAtRisk(key: PlanningConstraintKey, alertCodes: Set<number>): boolean {
  return alertCodes.has(RULE_ALERT_CODE[key])
}

const CONSTRAINT_ALERT_CODES = new Set<number>(Object.values(RULE_ALERT_CODE))

/**
 * True when the section carries at least one threshold-rule alert. The détail gate in ProjectDetail
 * uses this to keep the block reachable for rules that are ORTHOGONAL to the ready-check (M2/M3 can
 * fire on a fully « prêt » métré — review finding #1): the « Pas un souci ici » exit must always be
 * one click away from the alert that points at it.
 */
export function sectionHasConstraintAlert(alerts: { code: number }[]): boolean {
  return alerts.some((a) => CONSTRAINT_ALERT_CODES.has(a.code))
}

const reasonSchema = z.string().trim().min(5, 'Raison requise (≥ 5 caractères).')

interface ConstraintsBlockProps {
  projectId: string
  section: Section
  /** The section's alerts (Métré or Pose), used to derive at-risk per rule. */
  alerts: ProjectDetailAlertDto[]
  /** Called after any constraint mutation so the parent can refetch the project detail. */
  onMutated: () => void
  /** SOLY-PERM-001 — sans `planning:manage` les délais du chantier se lisent, ne se règlent pas.
   *  Régler UN chantier est un geste de planning ; couper une alerte pour toute l'organisation est
   *  un réglage d'entreprise et vit sur `settings:manage` (décision Thierry). */
  readOnly?: boolean
}

export function ConstraintsBlock({ projectId, section, alerts, onMutated, readOnly = false }: ConstraintsBlockProps) {
  const [rules, setRules] = useState<ProjectConstraintRuleDto[] | null>(null)
  const [open, setOpen] = useState(false)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    let active = true
    void (async () => {
      try {
        const dto = await planningApi.getProjectConstraints(projectId)
        if (active) setRules(dto.rules)
      } catch {
        if (active) setRules([])
      }
    })()
    return () => { active = false }
  }, [projectId, refresh])

  const alertCodes = new Set(alerts.map((a) => a.code))
  const sectionRules = (rules ?? []).filter((r) => belongsToSection(r.key, r.workItemKind, section))
  const atRiskCount = sectionRules.filter((r) => isAtRisk(r.key, alertCodes)).length

  // Refetch the constraints AND let the parent refetch the detail (alert/status updates) after a mutation.
  const afterMutation = () => { setRefresh((n) => n + 1); onMutated() }

  if (rules === null) return null

  return (
    <div className="mt-2 rounded-md border bg-background" data-testid={`planning-delais-${section}`}>
      <button
        type="button" onClick={() => setOpen((o) => !o)}
        data-testid={`planning-delais-${section}-header`}
        className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left">
        <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          Délais à respecter
        </span>
        <span className="flex items-center gap-1.5 text-[11px]">
          <span className={cn('h-[7px] w-[7px] flex-none rounded-full', atRiskCount > 0 ? 'bg-amber-500' : 'bg-muted-foreground/40')} />
          <span className={atRiskCount > 0 ? 'text-amber-600' : 'text-muted-foreground'}>
            {atRiskCount > 0 ? `${atRiskCount} à risque` : 'tous respectés'}
          </span>
        </span>
      </button>

      {open && (
        <ul className="space-y-2 border-t px-2 py-2">
          {sectionRules.map((r) => (
            <ConstraintLine
              key={r.key} projectId={projectId} rule={r} readOnly={readOnly}
              atRisk={isAtRisk(r.key, alertCodes)} onMutated={afterMutation} />
          ))}
        </ul>
      )}
    </div>
  )
}

// ── One rule line ───────────────────────────────────────────────────────────
type LineMode = 'view' | 'edit'

function ConstraintLine({
  projectId, rule, atRisk, onMutated, readOnly = false,
}: {
  projectId: string
  rule: ProjectConstraintRuleDto
  atRisk: boolean
  onMutated: () => void
  /** Sans `planning:manage` : la règle et son état se lisent, « modifier » et « revenir au défaut » disparaissent. */
  readOnly?: boolean
}) {
  const [mode, setMode] = useState<LineMode>('view')
  const testBase = `planning-delai-${rule.key}`

  const runPut = async (body: { valueDays?: number; dismiss: boolean; reason: string }) => {
    await planningApi.putProjectConstraint(projectId, rule.key, body)
    toast.success('Délai mis à jour')
    setMode('view')
    onMutated()
  }

  const reset = async () => {
    try {
      await planningApi.deleteProjectConstraint(projectId, rule.key)
      toast.success('Délai revenu au défaut')
      onMutated()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action échouée')
    }
  }

  return (
    <li className="text-xs" data-testid={testBase}>
      <div className="flex items-start gap-2">
        <span className={cn('mt-1 h-[7px] w-[7px] flex-none rounded-full', atRisk ? 'bg-amber-500' : 'bg-muted-foreground/40')} />
        <div className="flex-1">
          <p className="text-foreground">
            {RULE_SENTENCE[rule.key](rule.days)}{' '}
            {/* the editable value affordance (pencil on hover) */}
            {!readOnly && (
              <button
                type="button" onClick={() => setMode('edit')} data-testid={`${testBase}-edit`}
                className="group inline-flex items-center gap-0.5 align-baseline text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground">
                modifier
                <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            )}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {/* A dismissed rule must not read « Respecté » — it is ignored, possibly violated (review #2). */}
            <span className={atRisk ? 'text-amber-600' : 'text-muted-foreground'}>
              {rule.dismissed ? 'Ignorée' : atRisk ? 'À risque' : 'Respecté'}
            </span>
            {rule.source !== 'Default' && (readOnly ? (
              <span className="text-muted-foreground">modifié</span>
            ) : (
              <button
                type="button" onClick={() => void reset()} data-testid={`${testBase}-reset`}
                className="text-muted-foreground hover:text-foreground hover:underline">
                modifié · revenir au défaut
              </button>
            ))}
          </div>

          {mode === 'edit' && (
            <EditEditor
              testBase={testBase} initialDays={rule.days}
              onCancel={() => setMode('view')}
              onSubmit={(valueDays, reason) => runPut({ valueDays, dismiss: false, reason })} />
          )}
        </div>
      </div>
    </li>
  )
}

// ── Inline editor: new value + required reason ──────────────────────────────
function EditEditor({
  testBase, initialDays, onSubmit, onCancel,
}: {
  testBase: string
  initialDays: number
  onSubmit: (valueDays: number, reason: string) => Promise<void>
  onCancel: () => void
}) {
  const [days, setDays] = useState(initialDays)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (busy) return
    if (!Number.isFinite(days) || days < 0) { setError('Valeur invalide.'); return }
    const parsed = reasonSchema.safeParse(reason)
    if (!parsed.success) { setError(parsed.error.issues[0].message); return }
    setError(null); setBusy(true)
    try { await onSubmit(days, parsed.data) }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Action échouée') }
    finally { setBusy(false) }
  }

  return (
    <div className="mt-1.5 space-y-1.5 rounded-md border bg-muted/20 p-2" data-testid={`${testBase}-editor`}>
      <label className="block">
        <span className="text-[11px] uppercase text-muted-foreground">Nouvelle valeur (jours ouvrables)</span>
        <input
          type="number" min={0} step={1} value={days}
          onChange={(e) => setDays(Math.round(Number(e.target.value)))}
          data-testid={`${testBase}-value-input`}
          className="mt-0.5 w-full rounded border px-2 py-1 text-xs" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase text-muted-foreground">Raison (≥ 5 caractères)</span>
        <input
          type="text" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
          placeholder="Pourquoi ce changement ?"
          data-testid={`${testBase}-reason-input`}
          className="mt-0.5 w-full rounded border px-2 py-1 text-xs" />
      </label>
      {error && <p className="text-[11px] text-red-600" data-testid={`${testBase}-error`}>{error}</p>}
      <EditorFooter
        busy={busy} onCancel={onCancel} onSubmit={() => void submit()}
        submitLabel="Enregistrer" submitTestId={`${testBase}-submit`} cancelTestId={`${testBase}-cancel`} />
    </div>
  )
}

function EditorFooter({
  busy, onCancel, onSubmit, submitLabel, submitTestId, cancelTestId,
}: {
  busy: boolean
  onCancel: () => void
  onSubmit: () => void
  submitLabel: string
  submitTestId: string
  cancelTestId: string
}) {
  return (
    <div className="flex justify-end gap-2">
      <button type="button" onClick={onCancel} data-testid={cancelTestId}
        className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
      <button type="button" onClick={onSubmit} disabled={busy} data-testid={submitTestId}
        className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50">
        {submitLabel}
      </button>
    </div>
  )
}
